<script setup lang="ts">
import {computed} from "vue";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {formatAgendaTime, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {DEFAULT_APPOINTMENT_COLOR, SLOT_MINUTES} from "@/agenda/constants";
import {appointmentLabel, appointmentPatient} from "@/agenda/lib/appointmentLabel";

const props = defineProps<{
    appointment: AgendaAppointment;
    /** Force the one-line layout, for the narrow columns of week/team views. */
    compact?: boolean;
}>();

const color = computed(() => props.appointment.appointment_type?.color ?? DEFAULT_APPOINTMENT_COLOR);

const patientName = computed(() => appointmentPatient(props.appointment));

const label = computed(() => appointmentLabel(props.appointment));

const timeRange = computed(() => {
    return `${formatAgendaTime(props.appointment.start_at)} – ${formatAgendaTime(props.appointment.end_at)}`;
});

const durationMinutes = computed(() => {
    return parseAgendaDate(props.appointment.end_at).diff(props.appointment.start_at, 'minute');
});

const isCompact = computed(() => props.compact || durationMinutes.value < 30);

/**
 * A short appointment gets a card barely taller than one line of text: its
 * content is centered and the vertical padding dropped, otherwise the padding
 * pushes the text against the top edge and the overflow clips the rest. Longer
 * cards keep their content at the top, as a calendar is read.
 */
const isShort = computed(() => durationMinutes.value <= 2 * SLOT_MINUTES);
</script>

<template>
  <!--
    The tint is laid over an opaque white base: painted straight onto the grid,
    a 10 % colour would let the hour lines and the frequentation background
    show through the card.
  -->
  <div
    class="flex h-full flex-col overflow-hidden rounded-md border-l-4 bg-white px-2 text-xs shadow-sm"
    :class="isShort ? 'justify-center' : 'py-1'"
    :style="{borderLeftColor: color, backgroundImage: `linear-gradient(${color}1A, ${color}1A)`}"
  >
    <div v-if="isCompact" class="flex items-center gap-1.5 truncate">
      <span class="font-medium">{{ label }}</span>
      <span v-if="patientName" class="truncate">{{ patientName }}</span>
      <span class="ml-auto shrink-0 text-gray-500 tabular-nums">{{ timeRange }}</span>
    </div>
    <template v-else>
      <div class="flex items-baseline justify-between gap-1.5">
        <p class="truncate font-medium">
          {{ label }}
        </p>
        <span class="shrink-0 text-gray-500 tabular-nums">{{ timeRange }}</span>
      </div>
      <p v-if="patientName" class="truncate">
        {{ patientName }}
      </p>
      <span
        v-if="appointment.status === 'pending'"
        class="mt-0.5 inline-block rounded bg-white/70 px-1 text-[10px] font-medium text-gray-600"
      >
        À confirmer
      </span>
    </template>
  </div>
</template>
